// SKIM API Service
// Uses fetch() and getCookie('idToken') from ../utils/auth

import { getCookie } from '../utils/auth';
import { config } from '../utils/config';

const API_BASE_URL = config.skimApiUrl ? config.skimApiUrl() : 'https://tgavhq4vy7.execute-api.us-east-1.amazonaws.com/v1';

const getAuthHeaders = () => {
  const token = getCookie('idToken');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

// Clears cookies and redirects — same logic as the 401 branch in handleResponse
const sessionExpired = () => {
  document.cookie = 'idToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  document.cookie = 'accessToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  alert('Your session has expired. Please sign in again.');
  window.location.href = '/';
};

// Wraps fetch to handle the CORS-blocked-401 case:
// When the token cookie is gone, API Gateway returns 401 without CORS headers,
// so fetch() throws TypeError "Failed to fetch" before handleResponse can run.
const safeFetch = async (url, options) => {
  if (!getCookie('idToken')) {
    sessionExpired();
    throw new Error('Your session has expired. Please sign in again.');
  }

  try {
    return await fetch(url, options);
  } catch (err) {
    if (!getCookie('idToken')) {
      sessionExpired();
      throw new Error('Your session has expired. Please sign in again.');
    }
    throw new Error("Couldn't reach the service \u2014 please try again.");
  }
};

// Response interceptor — handles session expiry globally
const handleResponse = async (response, context) => {
  if (response.status === 401 || response.status === 403) {
    sessionExpired();
    throw new Error('Your session has expired. Please sign in again.');
  }
  if (!response.ok) {
    const errorText = await response.text();
    let message;
    try {
      const errorJson = JSON.parse(errorText);
      message = errorJson.message || errorJson.error || errorText;
    } catch {
      message = errorText;
    }
    const err = new Error(message);
    err.status = response.status;
    throw err;
  }
  return response.json();
};

export const skimApi = {
  // Initiate upload - get presigned URLs
  async initiateUpload(researchType, files, additionalContributors = [], route) {
    const body = {
      additional_contributors: additionalContributors,
      files: files.map(f => ({
        filename: f.name,
        file_type: f.name.endsWith('.docx') ? 'docx'
          : f.name.endsWith('.pdf') ? 'pdf'
          : 'pptx'
      }))
    };
    if (route) body.route = route;
    const response = await safeFetch(`${API_BASE_URL}/uploads`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });
    return handleResponse(response, 'Upload initiation');
  },

  // Upload file to S3 using presigned URL
  async uploadToS3(presignedUrl, file) {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });
    
    if (!response.ok) {
      throw new Error(`S3 upload failed: ${response.status}`);
    }
  },

  // Confirm upload (triggers agent processing)
  async confirmUpload(jobId) {
    const response = await safeFetch(`${API_BASE_URL}/uploads/${jobId}/confirm`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
    return handleResponse(response, 'Confirm upload');
  },

  // Get job status
  async getJobStatus(jobId) {
    const response = await safeFetch(`${API_BASE_URL}/jobs/${jobId}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response, 'Get job status');
  },

  // Get batch status
  async getBatchStatus(batchId) {
    const response = await safeFetch(`${API_BASE_URL}/batches/${batchId}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response, 'Get batch status');
  },

  // Publish job
  async publishJob(jobId, approvedTags) {
    const response = await safeFetch(`${API_BASE_URL}/jobs/${jobId}/publish`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ approved_tags: approvedTags }),
    });
    return handleResponse(response, 'Publish job');
  },

  // Get upload history
  async getHistory(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.status) queryParams.append('status', params.status);
    if (params.next_token) queryParams.append('next_token', params.next_token);
    
    const response = await safeFetch(`${API_BASE_URL}/jobs?${queryParams}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response, 'Get history');
  },

  // Get download URL for original file
  async getDownloadUrl(jobId) {
    const response = await safeFetch(`${API_BASE_URL}/jobs/${jobId}/download`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response, 'Get download URL');
  },

  // Delete job
  async deleteJob(jobId) {
    const response = await safeFetch(`${API_BASE_URL}/jobs/${jobId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    return handleResponse(response, 'Delete job');
  },

  // Convert a Point-in-time doc to Maintained (one-way; starts verification).
  // Server owns the rules (convert.py, gate fixed in CR-306671007): owner only,
  // route attested only, not archived.
  async convertJob(jobId) {
    const response = await safeFetch(`${API_BASE_URL}/jobs/${jobId}/convert`, {
      method: 'PUT',
      headers: getAuthHeaders(),
    });
    return handleResponse(response, 'Convert job');
  },

  // Extend a Point-in-time doc's archive date by N days from now.
  // Server owns the rules (ttl.py, gate fixed in CR-306671007): owner only,
  // attested only, active only, 1..365 days.
  async extendTtl(jobId, days) {
    const response = await safeFetch(`${API_BASE_URL}/jobs/${jobId}/ttl`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ days }),
    });
    return handleResponse(response, 'Extend TTL');
  },

  // Restore an archived job (un-archive). Server owns the rules (restore.py):
  // owner only, archived only; attested -> active + 30-day clock, checked ->
  // pending_verification + automatic re-verification.
  async restoreJob(jobId) {
    const response = await safeFetch(`${API_BASE_URL}/jobs/${jobId}/restore`, {
      method: 'PUT',
      headers: getAuthHeaders(),
    });
    return handleResponse(response, 'Restore job');
  },

  // R4: save the edited masking manifest — the server re-applies it over the
  // unmasked original and regenerates the masked content (masking_manifest.py).
  // This is how replacement edits AND added entities persist; the old path
  // (client-side string swap + saveMaskedContent) never taught the manifest.
  async saveMaskingManifest(jobId, manifest) {
    const response = await safeFetch(`${API_BASE_URL}/jobs/${jobId}/masking-manifest`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ masking_manifest: manifest }),
    });
    return handleResponse(response, 'Save masking manifest');
  },

  // Save masked content
  async saveMaskedContent(jobId, markdownContent) {
    const response = await safeFetch(`${API_BASE_URL}/jobs/${jobId}/masked-content`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ markdown_content: markdownContent }),
    });
    return handleResponse(response, 'Save masked content');
  },

  // Trigger verification for a maintained-route job
  async triggerVerify(jobId) {
    const response = await safeFetch(`${API_BASE_URL}/jobs/${jobId}/verify`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({}),
    });
    return handleResponse(response, 'Trigger verify');
  },

  // Get verification results (poll until results are ready)
  async getVerificationResults(jobId) {
    const response = await safeFetch(`${API_BASE_URL}/jobs/${jobId}/verification`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response, 'Get verification results');
  },

  // Submit a flag resolution (verb: 'apply' | 'override' | 'fix-myself')
  async submitVerifyResolution(jobId, sectionIndex, verb, options = {}) {
    const endpoint = verb === 'apply'
      ? `/jobs/${jobId}/verify/apply`
      : verb === 'fix-myself'
      ? `/jobs/${jobId}/verify/fix-myself`
      : `/jobs/${jobId}/verify/override`;

    const body = { section_index: sectionIndex };
    if (options.justification) body.justification = options.justification;
    if (options.note) body.note = options.note;

    const response = await safeFetch(`${API_BASE_URL}${endpoint}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });
    return handleResponse(response, `Verify ${verb}`);
  },

  // Get leaderboard
  async getLeaderboard(period = 'month') {
    const response = await safeFetch(`${API_BASE_URL}/leaderboard?period=${period}`, {
      headers: getAuthHeaders(),
    });
    return handleResponse(response, 'Get leaderboard');
  },
};

export default skimApi;
